import React, { useState, useEffect } from 'react';
import { apiClient } from '../../apiClient';

const Card = ({ children, className }) => (
  <div className={`bg-white p-6 rounded-lg shadow-sm ${className}`}>
    {children}
  </div>
);

// Translates action keys from the API into human-readable Japanese text.
const translateAction = (action) => {
  const translations = {
    // Invoice Actions
    'invoice_csv_upload': '請求書CSVアップロード',
    'invoice_processing_start': '請求書処理開始',
    'invoice_created': '請求書作成',
    'invoice_finalized': '請求書確定',
    'invoice_sent': '請求書送付',
    'scheduled_invoice_deleted': '予約済み請求書削除',
    'all_scheduled_invoices_deleted': '全予約済み請求書削除',
    'invoice_deleted': '請求書削除',

    // Customer Actions
    'customer_csv_upload': '顧客CSVアップロード',
    'customer_data_deleted': '顧客データ削除',
    'customer_delete': '顧客データ削除',
    'customer_profile_updated': 'お客様情報更新',

    // Member/Team Actions
    'member_invited': 'メンバー招待',
    'member_removed': 'メンバー削除',
    'member_profile_updated': 'メンバー情報更新',
    'member_permissions_updated': 'メンバー権限更新',
    'member_role_updated': 'メンバー権限更新',
    'user_login': 'ログイン',
    'member_login_success': 'ログイン',

    // Plan/Subscription Actions
    'plan_changed': 'プラン変更',
    'account_canceled': 'アカウント解約',
    'account_reactivated': 'ご利用再開',
    'account_reactivated_by_payment': 'お支払いによる利用再開',
    'account_past_due': '支払い遅延',
    'account_auto_suspended': '自動利用停止',
    'account_auto_terminated': '自動契約終了',
    'trial_started': 'トライアル開始',
    'trial_extended': 'トライアル延長',
    'trial_settings_updated': 'トライアル設定更新',
    'trial_ended_scheduled': 'トライアル終了予約',
    
    // Payment Method Actions
    'payment_method_added': '支払方法追加',
    'payment_method_removed': '支払方法削除',
    'payment_method_default_changed': 'デフォルト支払方法変更',

    // Settings / Tenant Management
    'stripe_api_key_updated': 'Stripe APIキー更新',
    'tax_rate_created': '税率ID登録',
    'tax_rate_updated': '税率ID更新',
    'tax_rate_deleted': '税率ID削除',
    'notification_settings_updated': 'メール受信設定更新',
    'billing_email_changed': '請求先メールアドレス変更',
    'tenant_suspended': 'アカウント利用停止',
    'tenant_resumed': 'アカウント利用再開',
    'tenant_invoice_locked': '請求書発行ロック',
    'tenant_invoice_unlocked': '請求書発行ロック解除',
    'user_disabled': 'アカウント無効化',
    'user_enabled': 'アカウント有効化',
    'user_force_logout': '強制ログアウト',

    // Fallback for unknown actions
    default: '不明な操作'
  };
  return translations[action] || action;
};

const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp._seconds ? new Date(timestamp._seconds * 1000) : new Date(timestamp);
    if (isNaN(date.getTime())) return '日付不明';

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}/${month}/${day} ${hours}:${minutes}`;
};

const RecentActivity = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRecentActivities = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/v1/activities/recent');

        if (!response.ok) {
          throw new Error(`Failed to fetch recent activities: ${response.status}`);
        }

        const data = await response.json();
        setActivities(data);
        setError(null);
      } catch (err) {
        console.error("Error fetching recent activities:", err);
        setError('アクティビティの取得に失敗しました。');
      } finally {
        setLoading(false);
      }
    };

    fetchRecentActivities();
  }, []);

  const renderContent = () => {
    if (loading) {
      return <div className="text-center text-gray-500">読み込み中...</div>;
    }

    if (error) {
      return <div className="text-center text-red-500">{error}</div>;
    }

    if (activities.length === 0) {
      return <div className="text-center text-gray-500">最近のアクティビティはありません</div>;
    }

    return (
      <ul className="space-y-3">
        {activities.map((activity) => (
          <li key={activity.id} className="flex justify-between items-center text-sm">
              <div className="flex items-center truncate">
                  <span className="font-semibold text-gray-700 w-20 truncate">{activity.actor_name}</span>
                  <span className="text-gray-600 ml-2 truncate">{translateAction(activity.action)}</span>
              </div>
              <span className="text-gray-500 text-xs flex-shrink-0 ml-2">{formatTimestamp(activity.timestamp)}</span>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <Card className="h-full">
      <h3 className="font-bold text-gray-800 mb-4">最近のアクティビティ</h3>
      {renderContent()}
    </Card>
  );
};

export default RecentActivity;
